import { useNavigate } from "react-router-dom";
import RMLogo from "@/assets/images/rm-logo-emblem.svg";
import { Typography } from "@/components/atoms";
import { useLayout, useOperator, useSubscriptionAssignment } from "@/hooks";
import { useAppSelector } from "@/redux/hooks";
import { operatorsSelector } from "@/redux/slices/operator/selectors";
import { clsx } from "@/utils";

interface SidebarLogoProps extends React.HTMLAttributes<HTMLDivElement> {
  ignoreSidebarOpen?: boolean;
  inlineLayout?: boolean;
}

export const SidebarLogo = ({ ignoreSidebarOpen, inlineLayout = false, className, ...props }: SidebarLogoProps) => {
  const navigate = useNavigate();
  const {
    layout: { sidebarOpen: isOpen },
  } = useLayout();
  const sidebarOpen = ignoreSidebarOpen ? true : isOpen;
  const operator = useOperator();
  const operators = useAppSelector(operatorsSelector);
  const logoUrl = operator?.images?.emblem ? operator.images.emblem : RMLogo;
  const { subscriptionStatus } = useSubscriptionAssignment();

  const handleClick = () => navigate(`/operator/${operator.id}/operators`);

  // Get plan name and icon from subscription assignment
  const planName = subscriptionStatus.baseSubscription?.subscription.name;
  const planIconSvg = subscriptionStatus.baseSubscription?.subscription.iconSvg;

  return (
    <div className={clsx(`${sidebarOpen ? "px-3" : "justify-center px-2.5"}`, className)} {...props}>
      <figure
        {...(operators.length > 1 && { onClick: handleClick })}
        className={clsx("flex flex-shrink-0 items-center rounded-lg py-5 sm:px-2.5", {
          "cursor-pointer hover:bg-primary-light": operators.length > 1,
          "place-content-center": !sidebarOpen,
        })}
      >
        <img className="h-8 w-8 flex-shrink-0 object-contain" src={logoUrl} alt="RideMinder" />
        {sidebarOpen && (
          <div
            className={clsx("ml-3 flex", {
              "flex-col": !inlineLayout,
              "items-center gap-2": inlineLayout,
            })}
          >
            <Typography variant="h3">{operator.name || "Rideminder"}</Typography>
            {planName && (
              <div className="flex items-center gap-1">
                {planIconSvg && (
                  <div className="h-4 w-4 flex-shrink-0 [&>svg]:h-4 [&>svg]:w-4" dangerouslySetInnerHTML={{ __html: planIconSvg }} />
                )}
                <Typography variant="small-regular" className="text-neutral-dark-gray">
                  {planName}
                </Typography>
              </div>
            )}
          </div>
        )}
      </figure>
    </div>
  );
};
